import { createSelector } from "@reduxjs/toolkit";
// Types
import Reducers from "types/Reducers";
// Model
import { RootState } from "store";
import IQuiz from "models/Quiz";
import IAnswer, { IAnswerResult } from "models/Answer";
import IBucket, { IBucketResult } from "models/Bucket";
import IPostQuizPage from "models/PostQuizPage";
import IQuestion from "models/Question";
// Selectors
import { selectQuestion } from "store/quiz/quizSelectors";

export const selectResult = (state:RootState) => state[Reducers.Player].result;

export const selectAnswers = (state:RootState) => state[Reducers.Player].answers;
export const selectBuckets = (state:RootState) => state[Reducers.Player].buckets;

export const selectEntryTime = (state:RootState) => state[Reducers.Player].entryTime;
export const selectAnswerTime = (state:RootState) => state[Reducers.Player].answerTime;

export const selectQuestionIndex = (state:RootState) => state[Reducers.Player].question.index;
export const selectQuestionReady = (state:RootState) => state[Reducers.Player].question.ready;
export const selectQuestionStart = (state:RootState) => state[Reducers.Player].question.start;
export const selectQuestionEnd = (state:RootState) => state[Reducers.Player].question.end;
export const selectQuestionAnswerPicked = (state:RootState) => state[Reducers.Player].question.answerPicked;
export const selectQuestionAnswerRefs = (state:RootState) => state[Reducers.Player].question.answerRefs;
export const selectIPLocationInfo = (state:RootState) => state[Reducers.Player].ipLocationInfo;

export const selectLeaderboard = createSelector(
  [ selectResult ],
  (result:any) => {
    if ( !result ) return [];
    if ( !result.results || result.results.length === 0 ) return [];
    return [...result.results].sort((a:any, b:any) => b.score - a.score);
  }
);

export const selectTopBucket = createSelector(
  [ selectResult ],
  (result:any) => {
    if ( !result || !result.currentUserResult ) return null;
    const { buckets } = result.currentUserResult;
    if ( !buckets || !buckets.length ) return null;
    return [...buckets].reduce((acc:IBucketResult, cur:IBucketResult) => {
      return (acc.score > cur.score) ? acc : cur
    });
  }
);

export const selectPostQuizPageByTopBucket = createSelector(
  [
    // selectQuiz,
    // ToDO: For some reason selector throw an error with `circular dependency issue`
    (state:RootState) => state.quiz.quiz,
    selectTopBucket
  ],
  (quiz:IQuiz | null, bucket:IBucketResult | null) => {
    if ( !quiz || !bucket ) return undefined;
    if ( !quiz.postQuizPages ) return undefined;
    return quiz.postQuizPages.find((postQuizPage:IPostQuizPage) => postQuizPage.condition.value === bucket.ref)
  }
);

export const selectTotalScore = createSelector(
  [ selectAnswers ],
  (answers:IAnswerResult[]) => {
    if ( !answers.length ) return 0;
    return answers.reduce((acc:number, cur:IAnswerResult) => {
      if ( cur.correct !== false ) acc = acc + (cur.score || 0);
      return acc;
    }, 0);
  }
);

export const selectCurrentQuestionIndex = createSelector(
  [ selectQuestionIndex ], (questionIndex:number) => questionIndex + 1
);

export const selectIsAnyAnswerCorrect = createSelector(
  [
    selectQuestion
  ],
  (question) => {
    if ( !question ) return false;

    return question.answers.every((answer:IAnswer) => !answer.correct);
  }
);

export const selectIsAnswerRefInRefs = createSelector(
  [
    selectQuestionAnswerRefs,
    (_, props:{ answerRef:string }) => props
  ],
  (questionAnswerRefs:string[], { answerRef }) => questionAnswerRefs.includes(answerRef)
);

export const selectAnswersByAnswerRefs = createSelector(
  [
    selectQuestion,
    selectQuestionAnswerRefs,
    selectAnswerTime
  ],
  (question:IQuestion, questionAnswerRefs:string[], time) => {
    if ( !question ) return [];
    if ( !questionAnswerRefs.length ) return [];

    const filteredAnswers = question.answers.filter((answer:IAnswer) => questionAnswerRefs.includes(answer.ref));

    return filteredAnswers.map(({ ref, title }:IAnswer) => ({
      ref,
      title,
      time,
      question: {
        ref: question.ref,
        title: question.title
      }
    }));
  }
);

export const selectBucketsByAnswerRefs = createSelector(
  [
    // selectQuiz,
    // ToDO: For some reason selector throw an error with `circular dependency issue`
    (state:RootState) => state.quiz.quiz,
    selectQuestion,
    selectQuestionAnswerRefs
  ],
  (quiz:IQuiz | null, question:IQuestion, questionAnswerRefs:string[]) => {
    if ( !quiz ) return [];
    if ( !questionAnswerRefs.length ) return [];

    const filteredAnswers = question?.answers.filter((answer:IAnswer) => questionAnswerRefs.includes(answer.ref)) || [];

    const buckets:{ [key:string]:IBucketResult } = quiz.buckets.reduce((acc:{ [key:string]:IBucketResult }, cur:IBucket) => {
      acc[cur.ref] = {...cur, score: 0};
      return acc;
    }, {});

    for ( let answer of filteredAnswers ){
      for ( let bucket of answer.bucketScores ){
        if ( buckets[bucket.ref] ){
          const score = buckets[bucket.ref]['score'];
          buckets[bucket.ref]['score'] = score + (bucket.score || 0);
        }
      }
    }

    return buckets;
  }
);
